import { LoggerService } from '@ad-lucy/components/LoggerService';
import { useFetch } from '@ad-lucy/hooks/fetch/useFetch';
import { type Context, type ReactElement, useEffect, useMemo, useRef, useState } from 'react';

import type { AdDataContextValue } from './types';

export type Transformer<RawResponseType, TransformedResponseType> = (
  data: RawResponseType,
) => TransformedResponseType;

interface DataProviderProps<TRaw, TData> {
  readonly url: string;
  readonly DataContext: Context<AdDataContextValue<TData>>;
  readonly children: ReactElement | ReactElement[];
  readonly transformer?: Transformer<TRaw, TData>;
  readonly load: boolean;
}

export function DataProvider<TRaw, TData>(props: DataProviderProps<TRaw, TData>): ReactElement {
  const { url, DataContext, transformer, children, load } = props;
  const [data, setData] = useState<TData>();
  const { get, loading, error } = useFetch<TRaw>();
  const fetchRequestStarted = useRef('');

  const logger = useMemo(() => LoggerService.getLogger().getModuleLogger('DataProvider'), []);

  useEffect(() => {
    //Dont fetch the same url twice
    if (load && fetchRequestStarted.current !== url) {
      fetchRequestStarted.current = url;
      get(url)
        .then(transformer)
        .then(setData)
        .catch(() => logger.error(`Failed to load '${url}'`));
    }
  }, [get, load, url, transformer, logger]);

  const contextValue = useMemo(() => {
    return {
      data: {
        loading,
        error: error?.message,
        data: error ? undefined : data,
      },
    };
  }, [data, loading, error]);

  return <DataContext.Provider value={contextValue}>{children}</DataContext.Provider>;
}
